import { Inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { AnalyticEvent, AnalyticsBloc } from '@kanalytics';
import { SettingsBloc, SharingBloc, TagBloc, ThemeBloc } from '@kbloc';
import { TranslationBloc } from '@kf-loc';
import { NotificationsBloc } from '@kp/notifications/notifications.bloc';
import { VersionBloc } from '@kp/version/version.bloc';
import { DataStoreService, ThemeService, WINDOW } from '@kservice';
import { Status } from '@ktypes/models';
import { NavigationService } from '@kutil';
import { Subject, combineLatest } from 'rxjs';
import { filter, skipWhile, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AppLifecycleService {
  constructor(
    private _analyticsBloc: AnalyticsBloc,
    private _dataStoreService: DataStoreService,
    navigation: NavigationService, // do not delete, triggers NavigationService tracking navigation
    notificationsBloc: NotificationsBloc, // do not delete, sets up listener for web service token changes
    private _router: Router,
    private _settingsBloc: SettingsBloc,
    private _sharingBloc: SharingBloc,
    private _tagBloc: TagBloc,
    themeBloc: ThemeBloc,
    themeService: ThemeService, // do not delete, here to trigger ThemeService init
    private _translationBloc: TranslationBloc,
    private _transloco: TranslocoService,
    private _versionBloc: VersionBloc,
    @Inject(WINDOW) private _window: Window
  ) {
    themeBloc.themesStatus$
      .pipe(
        filter((themeStatus) => themeStatus?.status === Status.done),
        take(1)
      )
      .subscribe((themesStatus) => {
        themeService.updateAvailableThemes(themesStatus?.data);
      });
    themeBloc.getThemes();
  }

  private _destroy$ = new Subject<void>();

  startup() {
    if (this._window.location.href.includes('/delete') || this._window.location.href.includes('/unsubscribe')) {
      // if on delete or unsubscribe path, don't kick off lifecycle events
      return;
    }

    // kick off analytics
    this._analyticsBloc.initialize();

    this._analyticsBloc.sendEvent(
      this._analyticsBloc.createEventFromEvent({
        event: AnalyticEvent.app_open,
      })
    );

    // ENTER/EXIT PAGE: subscribe to router page visit nav end events and check required app version(s)
    this._router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(this._pageChange.bind(this));

    // Wait until a token is available and authStatus is not an error before making the calls requiring a token
    combineLatest([this._dataStoreService.authData$, this._dataStoreService.authStatus$])
      .pipe(
        skipWhile(
          ([authData, authStatus]) =>
            !authData?.token || authStatus?.status == null || authStatus?.status !== Status.done
        ),
        take(1)
      )
      .subscribe(this._initWithToken.bind(this));
  }

  private _initWithToken() {
    this._settingsBloc.getUserSettings();
    this._tagBloc.getUserTags();
    this._versionBloc.checkAppVersion();
    this._sharingBloc.getInvites();

    combineLatest([this._tagBloc.languageTranslation$, this._dataStoreService.user$])
      .pipe(
        filter(([languageTranslation, user]) => languageTranslation && !!user.settings?.language),
        take(1)
      )
      .subscribe(([, user]) => {
        // get supported languages, wait before proceeding
        this._translationBloc.getSupportedLanguages().then(() => {
          this._tagBloc.languageTranslation$.pipe(take(1)).subscribe((languageTranslation) => {
            if (languageTranslation) {
              this._translationBloc.checkUserLanguage();
              this._transloco.load(this._dataStoreService.signalStore?.currentLanguage());
            }
          });
        });
      });
  }

  private _pageChange() {
    this._versionBloc.checkAppVersion();
  }

  shutdown() {
    this._destroy$.next();
    this._destroy$.complete();
    if (this._window.location.href.includes('/delete') || this._window.location.href.includes('/unsubscribe')) {
      // if on delete or unsubscribe path, lifecycle events where never initialized, just exit
      return;
    }
    this._analyticsBloc.sendEvent(
      this._analyticsBloc.createEventFromEvent({
        event: AnalyticEvent.app_exit,
      })
    );
  }
}
